/**
 * ESC/POS printer helpers via QZ Tray.
 *
 * Architecture:
 *   web app (Chrome) --wss--> QZ Tray (local Java daemon on POS PC) --USB--> Rongta ESC/POS printer
 *
 * Requires QZ Tray installed on the POS device:  https://qz.io/download/
 * On first use the browser will prompt to trust QZ Tray's self-signed cert — accept once.
 */
import qz from "qz-tray";

const RECEIPT_WIDTH = 32; // 80mm paper = ~32 chars in default font A
const LS_PRINTER_KEY = "gh_qz_printer";

// ------- ESC/POS byte helpers -------
const ESC = "\x1B";
const GS = "\x1D";
const INIT = ESC + "@";
const ALIGN_LEFT = ESC + "a" + "\x00";
const ALIGN_CENTER = ESC + "a" + "\x01";
const ALIGN_RIGHT = ESC + "a" + "\x02";
const BOLD_ON = ESC + "E" + "\x01";
const BOLD_OFF = ESC + "E" + "\x00";
const DOUBLE_ON = GS + "!" + "\x11"; // double width + double height
const DOUBLE_OFF = GS + "!" + "\x00";
const CUT = GS + "V" + "\x42" + "\x00"; // full cut
const LF = "\n";

// Customer-controlled text (name, phone, scheduled_for) is streamed as raw
// bytes to the printer. Strip control chars so a payload like ESC@ can't
// reset the print head, feed extra paper, or fire the cash drawer kick pin.
const sanitizePrint = (s) =>
  String(s ?? "").replace(/[\x00-\x1F\x7F]/g, " ").slice(0, 60);

const pad = (left, right, width = RECEIPT_WIDTH) => {
  const l = String(left);
  const r = String(right);
  const space = Math.max(1, width - l.length - r.length);
  return l + " ".repeat(space) + r;
};

const divider = "-".repeat(RECEIPT_WIDTH);

const dollars = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * Render a "GAITHER COWBOYS" text logo on an offscreen canvas and return the
 * raw base64 PNG (no data-URI prefix) that QZ Tray can rasterize into
 * ESC/POS bit-image commands.
 *
 * Kept ≤ 512px wide to fit comfortably on 80mm thermal paper (576 dot max).
 */
export function buildLogoBase64() {
  const width = 512;
  const height = 140;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  // White background — thermal printers only print black on paper white.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = "#000000";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  // "GAITHER" — big condensed serif
  ctx.font = "900 64px Georgia, 'Times New Roman', serif";
  ctx.fillText("GAITHER", width / 2, 44);

  // Divider bars flanking "COWBOYS"
  ctx.fillRect(40, 92, 90, 4);
  ctx.fillRect(width - 130, 92, 90, 4);

  // "COWBOYS" — bold sans, spaced letters
  ctx.font = "800 42px Impact, 'Arial Black', sans-serif";
  ctx.fillText("C O W B O Y S", width / 2, 96);

  // Tagline
  ctx.font = "600 18px Arial, sans-serif";
  ctx.fillText("ATHLETIC BOOSTERS  •  CONCESSIONS", width / 2, 128);

  // Strip the "data:image/png;base64," prefix — QZ Tray wants raw b64.
  return canvas.toDataURL("image/png").split(",")[1];
}

/**
 * Build the ESC/POS payload for a single order.
 * Follows the sample layout: centered header, dashed dividers,
 * left/right-aligned line items, bold TOTAL, centered footer, cut.
 */
export function buildReceipt(order) {
  const parts = [];
  parts.push(INIT);

  // Header — bold, double-size, centered
  parts.push(ALIGN_CENTER);
  parts.push(BOLD_ON + DOUBLE_ON);
  parts.push(String(order.order_number) + LF);
  parts.push(DOUBLE_OFF);
  parts.push(BOLD_OFF);
  parts.push("Express Window" + LF);
  parts.push(ALIGN_LEFT);
  parts.push(divider + LF);

  // Customer + timestamp
  const ts = order.created_at
    ? new Date(order.created_at).toLocaleString()
    : new Date().toLocaleString();
  parts.push(`${sanitizePrint(order.customer_name)}` + LF);
  parts.push(`${sanitizePrint(order.customer_phone)}` + LF);
  parts.push(ts + LF);
  // Delayed pickup — print big so the line staff notices they shouldn't
  // start cooking this until closer to the customer's requested time.
  if (order.scheduled_for) {
    parts.push(divider + LF);
    parts.push(BOLD_ON);
    parts.push(`PICKUP: ${sanitizePrint(order.scheduled_for).toUpperCase()}` + LF);
    parts.push(BOLD_OFF);
  }
  parts.push(divider + LF);

  // Items
  for (const it of order.items || []) {
    const qty = it.quantity || 1;
    const label = `${qty}x ${sanitizePrint(it.name)}`;
    const lineTotal = dollars((it.price || 0) * qty);
    parts.push(pad(label, lineTotal) + LF);
    const extras = [it.variant_choice, it.side_choice, it.drink_choice]
      .filter(Boolean)
      .map(sanitizePrint)
      .join(" / ");
    if (extras) {
      parts.push("   " + extras + LF);
    }
    // Add-ons: one line each so the kitchen can't miss them.
    for (const a of it.addons || []) {
      const qtyLabel = (a.qty || 1) > 1 ? ` x${a.qty}` : "";
      parts.push("   + " + sanitizePrint(a.label) + qtyLabel + LF);
    }
  }
  parts.push(divider + LF);

  // Totals block
  parts.push(pad("Subtotal", dollars(order.subtotal)) + LF);
  if (order.surcharge > 0) parts.push(pad("Online fee", dollars(order.surcharge)) + LF);
  if (order.tip > 0) parts.push(pad("Tip", dollars(order.tip)) + LF);
  parts.push(BOLD_ON);
  parts.push(pad("TOTAL", dollars(order.total)) + LF);
  parts.push(BOLD_OFF);
  parts.push(divider + LF);

  // Footer
  parts.push(ALIGN_CENTER);
  parts.push("Thank You!" + LF);
  parts.push("Go Cowboys!" + LF);
  parts.push(LF + LF + LF); // feed
  parts.push(CUT);

  return parts.join("");
}

// ------- QZ Tray connection lifecycle -------

let connectPromise = null;

/** Connect to the local QZ Tray daemon (once). */
export async function connectQz() {
  if (qz.websocket.isActive()) return true;
  if (connectPromise) return connectPromise;
  // Unsigned mode — user must click Allow on first connection.
  qz.security.setCertificatePromise((resolve) => resolve(null));
  qz.security.setSignaturePromise(() => (resolve) => resolve(null));
  connectPromise = qz.websocket
    .connect({ retries: 1, delay: 1 })
    .then(() => true)
    .catch((err) => {
      connectPromise = null;
      throw err;
    });
  return connectPromise;
}

export function isConnected() {
  try {
    return qz.websocket.isActive();
  } catch {
    return false;
  }
}

export async function disconnectQz() {
  if (qz.websocket.isActive()) {
    await qz.websocket.disconnect();
  }
}

/** Returns array of printer names known to the OS. */
export async function listPrinters() {
  await connectQz();
  return qz.printers.find();
}

/** Send raw ESC/POS bytes to the selected printer. */
export async function printReceipt(order, printerName) {
  await connectQz();
  const target = printerName || getSavedPrinter();
  if (!target) throw new Error("No printer selected");
  const config = qz.configs.create(target);

  // Two-part payload: logo image (rasterized to ESC/POS by QZ Tray) followed
  // by the plain-text receipt body. Order matters — logo prints first.
  const data = [
    {
      type: "raw",
      format: "image",
      flavor: "base64",
      data: buildLogoBase64(),
      options: { language: "escpos", dotDensity: "double" },
    },
    { type: "raw", format: "plain", data: buildReceipt(order) },
  ];
  await qz.print(config, data);
}

// ------- Saved printer preference (localStorage) -------

export function getSavedPrinter() {
  return localStorage.getItem(LS_PRINTER_KEY) || "";
}

export function saveSavedPrinter(name) {
  if (name) localStorage.setItem(LS_PRINTER_KEY, name);
  else localStorage.removeItem(LS_PRINTER_KEY);
}
